import type { ReactNode } from 'react';
import { cn } from '../lib/utils.ts';

/**
 * Ausgegrautes Element ohne Funktion oder Daten (Ticket 1.247, Change „Entwürfe ins ERP“ §3): sichtbar wie im Entwurf,
 * aber grau, `aria-disabled` und mit Hinweis „kommt mit Story X.Y“ bzw. „noch nicht gebaut“ als Tooltip (`title`) und
 * für Hilfsmittel. Tests und Bildvergleich-Masken finden es über `data-testid="noch-nicht-gebaut"`.
 */
export function notYetBuiltText(story?: string): string {
  return story ? `kommt mit Story ${story}` : 'noch nicht gebaut';
}

export function NotYetBuilt({
  story,
  children,
  className,
  as: Tag = 'span',
  showHint = false,
}: {
  /** Story, die die Funktion bringt (z. B. „5.43“); ohne Angabe „noch nicht gebaut“. */
  story?: string;
  children: ReactNode;
  className?: string;
  as?: 'span' | 'div' | 'td' | 'section';
  /** Hinweis zusätzlich sichtbar als kleine Zeile unter dem Inhalt. */
  showHint?: boolean;
}) {
  const hint = notYetBuiltText(story);
  // `relative`: der `sr-only`-Hinweis bleibt im Element (sonst ragt er z. B. aus der wischbaren Kachelreihe am Handy).
  return (
    <Tag
      data-testid="noch-nicht-gebaut"
      data-story={story}
      aria-disabled="true"
      title={hint}
      className={cn('relative cursor-not-allowed text-ink-3 opacity-60', Tag === 'span' ? 'inline-flex flex-col' : '', className)}
    >
      {children}
      <span className={showHint ? 'text-caption text-ink-3' : 'sr-only'}>{` (${hint})`}</span>
    </Tag>
  );
}

/** Ausgegrauter Knopf (Entwurf zeigt ihn, Funktion fehlt): `aria-disabled`, nicht klickbar, Hinweis als Tooltip. */
export function NotYetBuiltButton({ story, children, className }: { story?: string; children: ReactNode; className?: string }) {
  const hint = notYetBuiltText(story);
  return (
    <button
      type="button"
      data-testid="noch-nicht-gebaut"
      data-story={story}
      aria-disabled="true"
      title={hint}
      aria-label={typeof children === 'string' ? `${children} (${hint})` : undefined}
      onClick={(e) => e.preventDefault()}
      className={cn('relative inline-flex cursor-not-allowed items-center gap-space-1 opacity-60', className)}
    >
      {children}
    </button>
  );
}
